import React, { VFC, useEffect, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';

import Dialog from '../Dialog';
import DialogContent from '../Dialog/DialogContent';
import DialogTitleWithCloseButton from '../Dialog/DialogTitleWithCloseButton';
import PointSpImage from '../../../../images/front/user_page_announcement/point_sp.png';
import PointPcImage from '../../../../images/front/user_page_announcement/point_pc.png';

// グローバルな状態で、一度だけ表示されるようにする
let isDialogOpen = false;
let openDialogInstance: string | null = null;

const OrangeTitle = styled.div`
  color: #ff6b35;
  font-weight: 700;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
`;

const ContentText = styled(Typography)`
  line-height: 24px;
  font-size: 14px;
  font-weight: 300;
  line-height: 24px;
`;

const CenteredButton = styled(Button)`
  min-width: 122px;
  height: 48px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 999px;
  text-align: center;
  &.MuiButton-containedPrimary {
    background-color: #ff6e2e;
    &:hover {
      color: #fff !important;
      background-color: #ff3c10;
    }
  }
`;

const SecondaryButton = styled(Button)`
  padding: 12px 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
  min-width: fit-content;
  &.MuiButton-containedPrimary {
    background-color: #ff6e2e;
    &:hover {
      color: #fff !important;
      background-color: #ff3c10;
    }
  }
`;

const PointsSection = styled(Box)`
  margin-bottom: 40px;
`;

type Props = {
  open: boolean;
  hasExperienceReview: boolean;
  onClose: () => void;
};

const UserPageAnnouncementDialog: VFC<Props> = ({
  open,
  hasExperienceReview,
  onClose,
}: Props) => {
  const instanceId = useRef(Math.random().toString(36));
  const [actualOpen, setActualOpen] = React.useState(false);

  useEffect(() => {
    if (open) {
      // 既に他のインスタンスが開いている場合は、このインスタンスは開かない
      if (isDialogOpen && openDialogInstance !== instanceId.current) {
        return;
      }
      isDialogOpen = true;
      openDialogInstance = instanceId.current;
      setActualOpen(true);
    } else {
      if (openDialogInstance === instanceId.current) {
        isDialogOpen = false;
        openDialogInstance = null;
      }
      setActualOpen(false);
    }
  }, [open]);

  const handleClose = () => {
    if (openDialogInstance === instanceId.current) {
      isDialogOpen = false;
      openDialogInstance = null;
    }
    setActualOpen(false);
    onClose();
  };

  const handleEditProfile = () => {
    onClose();
  };

  if (hasExperienceReview) {
    // 体験記ありの場合
    return (
      <Dialog
        open={actualOpen}
        PaperProps={{ style: { maxWidth: 600, borderRadius: '20px' } }}
      >
        <DialogTitleWithCloseButton
          onClose={handleClose}
          style={{ height: '52px' }}
        />
        <DialogContent style={{ padding: '0 16px 40px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '24px',
            }}
          >
            <OrangeTitle>
              公開プロフィールを
              <br />
              編集できるようになりました！
            </OrangeTitle>
            <div style={{ textAlign: 'center' }}>
              <ContentText variant="body2">
                いつもご利用ありがとうございます。
              </ContentText>
              <ContentText
                variant="body2"
                style={{ whiteSpace: 'pre-wrap', fontWeight: 600 }}
              >
                体験記とプロフィールを
                <br />
                ひとつのページでまとめて
                <br />
                紹介できるようになりました。
              </ContentText>
            </div>
          </div>
          <PointsSection>
            <picture>
              <source srcSet={PointSpImage} media="(max-width: 600px)" />
              <img src={PointPcImage} alt="points" width="100%" />
            </picture>
          </PointsSection>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              alignItems: 'center',
            }}
          >
            <SecondaryButton
              // @ts-ignore
              component={Link}
              href="/mypage/profile"
              color="primary"
              variant="contained"
              disableElevation
              onClick={handleEditProfile}
            >
              公開プロフィールを編集する
            </SecondaryButton>
            <SecondaryButton
              variant="outlined"
              disableElevation
              onClick={handleClose}
              style={{ color: '#171717', borderColor: '#D4D4D4' }}
            >
              閉じる
            </SecondaryButton>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // 体験記なしの場合
  return (
    <Dialog
      open={actualOpen}
      PaperProps={{ style: { maxWidth: 600, borderRadius: '20px' } }}
    >
      <DialogTitleWithCloseButton
        onClose={handleClose}
        style={{ height: '52px' }}
      />
      <DialogContent style={{ padding: '0 16px 40px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          <OrangeTitle>
            公開プロフィール機能が
            <br />
            できました!!
          </OrangeTitle>
          <ContentText variant="body2">
            おてつたび参加時に書ける「体験記」をひとつのページでまとめて見られるようになりました。
          </ContentText>
          <ContentText variant="body2">
            まずは、ほかの人のページを見てどんな体験があるのか知ってみてください。
          </ContentText>
          <ContentText variant="body2">
            体験記を投稿すると、あなたの公開プロフィールが作られます。
          </ContentText>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CenteredButton
            color="primary"
            variant="contained"
            disableElevation
            onClick={handleClose}
          >
            OK
          </CenteredButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserPageAnnouncementDialog;
